import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';

export const NPC_HARVEST_X = -18;
export const NPC_HARVEST_Z = -12;
export const HARVEST_QUEST_TARGET = 120;
export const HARVEST_QUEST_MAT_REWARD = 80;
export const HARVEST_QUEST_HP_REWARD = 25;

const NEAR_RADIUS = 4.5;
const NEAR_RADIUS_SQ = NEAR_RADIUS * NEAR_RADIUS;
const INTERACT_RANGE = 4.2;
const LOOK_DOT_MIN = 0.78;

export type NpcHarvestQuestState = 'available' | 'accepted' | 'completed';

/** Quest giver at Shifty Shafts — harvest materials quest per match. */
export class FpsNpcQuestHarvest {
  private readonly group: THREE.Group;
  private readonly markerSprite: THREE.Sprite;
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly actionBtn: HTMLButtonElement;
  private readonly toonGrad: THREE.DataTexture;
  private visible = false;
  private state: NpcHarvestQuestState = 'available';
  private progress = 0;
  private bobPhase = Math.random() * Math.PI * 2;

  constructor(
    sceneRoot: THREE.Group,
    mount: HTMLElement,
    private readonly onAccept: () => void,
    private readonly onComplete: () => void,
  ) {
    this.toonGrad = createToonGradientMap();
    this.group = new THREE.Group();
    this.group.position.set(NPC_HARVEST_X, 0, NPC_HARVEST_Z);
    this.group.rotation.y = Math.PI * 0.55;
    this.buildNpcMesh();
    this.markerSprite = this.createQuestMarker();
    this.group.add(this.markerSprite);
    sceneRoot.add(this.group);

    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:95',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(6,10,16,0.58)',
      'backdrop-filter:blur(7px)',
      '-webkit-backdrop-filter:blur(7px)',
      'pointer-events:auto',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'min-width:300px',
      'padding:18px 20px 16px',
      'border-radius:14px',
      'background:linear-gradient(165deg,rgba(24,28,42,0.97),rgba(10,14,24,0.98))',
      'border:2px solid rgba(120,200,120,0.62)',
      'box-shadow:0 10px 36px rgba(0,0,0,0.52),inset 0 1px 0 rgba(255,255,255,0.05)',
      'color:#e8f4ff',
      'font:14px system-ui,Segoe UI,sans-serif',
      'pointer-events:auto',
    ].join(';');

    this.titleEl = document.createElement('div');
    this.titleEl.style.cssText =
      'font-weight:700;font-size:15px;letter-spacing:0.05em;margin-bottom:4px;color:#9dff9d';
    this.titleEl.textContent = 'Foreman Flint';
    this.panel.appendChild(this.titleEl);

    const sub = document.createElement('div');
    sub.textContent = 'Shifty Shafts';
    sub.style.cssText = 'opacity:0.72;font-size:12px;margin-bottom:12px';
    this.panel.appendChild(sub);

    this.bodyEl = document.createElement('div');
    this.bodyEl.style.cssText = 'margin-bottom:14px;font-size:13px;line-height:1.45;color:#d8e8ff';
    this.panel.appendChild(this.bodyEl);

    this.actionBtn = document.createElement('button');
    this.actionBtn.type = 'button';
    this.actionBtn.style.cssText = [
      'width:100%',
      'padding:10px 12px',
      'border-radius:10px',
      'border:1px solid rgba(120,200,120,0.45)',
      'background:rgba(12,28,18,0.88)',
      'color:#c8ffc8',
      'font:700 13px system-ui,sans-serif',
      'cursor:pointer',
    ].join(';');
    this.actionBtn.addEventListener('click', () => this.handleAction());
    this.panel.appendChild(this.actionBtn);

    const foot = document.createElement('div');
    foot.textContent = 'Esc — close';
    foot.style.cssText = 'margin-top:10px;text-align:center;opacity:0.55;font-size:11px';
    this.panel.appendChild(foot);

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);
    this.refreshDialog();
  }

  private buildNpcMesh(): void {
    const skin = toonMat(0xc49464, this.toonGrad, { emissive: 0x221408, emissiveIntensity: 0.12 });
    const vest = toonMat(0xff8c42, this.toonGrad, { emissive: 0x402010, emissiveIntensity: 0.22 });
    const pants = toonMat(0x3a3a44, this.toonGrad, { emissive: 0x080c14, emissiveIntensity: 0.1 });

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.74, 0.34), vest);
    body.position.y = 1.08;
    body.castShadow = true;
    this.group.add(body);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.36, 0.36), skin);
    head.position.y = 1.64;
    head.castShadow = true;
    this.group.add(head);

    const helmet = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.14, 0.4), toonMat(0xffcc44, this.toonGrad));
    helmet.position.y = 1.88;
    this.group.add(helmet);

    const legGeo = new THREE.BoxGeometry(0.16, 0.52, 0.16);
    for (const side of [-1, 1] as const) {
      const leg = new THREE.Mesh(legGeo, pants);
      leg.position.set(side * 0.14, 0.48, 0);
      leg.castShadow = true;
      this.group.add(leg);
    }

    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.62, 0.08, 12),
      toonMat(0x5a6a50, this.toonGrad, { emissive: 0x182018, emissiveIntensity: 0.1 }),
    );
    platform.position.y = 0.04;
    platform.receiveShadow = true;
    this.group.add(platform);
  }

  private createQuestMarker(): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#7dff7d';
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#0a180a';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = '#0a180a';
    ctx.font = 'bold 36px system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⛏', 32, 34);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.55, 0.55, 1);
    sprite.position.y = 2.15;
    return sprite;
  }

  resetForMatch(): void {
    this.state = 'available';
    this.progress = 0;
    this.close();
    this.markerSprite.visible = true;
    this.refreshDialog();
  }

  getState(): NpcHarvestQuestState {
    return this.state;
  }

  getHudText(): string {
    if (this.state === 'accepted') {
      return `Quest: Harvest ${this.progress}/${HARVEST_QUEST_TARGET} mats`;
    }
    return '';
  }

  isNear(px: number, pz: number): boolean {
    const dx = px - NPC_HARVEST_X;
    const dz = pz - NPC_HARVEST_Z;
    return dx * dx + dz * dz <= NEAR_RADIUS_SQ;
  }

  getNearHint(): string {
    if (this.state === 'completed') return '';
    return this.state === 'accepted' ? '[E] Quest progress' : '[E] Foreman Flint';
  }

  shouldShowPrompt(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    const center = new THREE.Vector3(NPC_HARVEST_X, 1.2, NPC_HARVEST_Z);
    const toNpc = center.clone().sub(playerPos);
    const dist = toNpc.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;
    const dir = toNpc.multiplyScalar(1 / dist);
    return cameraDir.clone().normalize().dot(dir) > LOOK_DOT_MIN;
  }

  open(): void {
    if (this.state === 'completed') return;
    this.visible = true;
    this.backdrop.style.display = 'flex';
    this.refreshDialog();
  }

  close(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isOpen(): boolean {
    return this.visible;
  }

  tryOpenFromInteract(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    if (!this.shouldShowPrompt(playerPos, cameraDir) && !this.isNear(playerPos.x, playerPos.z)) {
      return false;
    }
    this.open();
    return true;
  }

  recordHarvest(mats: number): void {
    if (this.state !== 'accepted' || mats <= 0) return;
    this.progress = Math.min(HARVEST_QUEST_TARGET, this.progress + mats);
    if (this.progress >= HARVEST_QUEST_TARGET) {
      this.state = 'completed';
      this.markerSprite.visible = false;
      this.close();
      this.onComplete();
    }
    this.refreshDialog();
  }

  update(dt: number): void {
    if (this.state === 'completed') return;
    if (!Number.isFinite(dt) || dt <= 0) dt = 0;
    this.bobPhase += dt * 2.2;
    this.markerSprite.position.y = 2.15 + Math.sin(this.bobPhase) * 0.08;
  }

  dispose(): void {
    this.backdrop.remove();
    this.group.removeFromParent();
    this.toonGrad.dispose();
    const markerMat = this.markerSprite.material as THREE.SpriteMaterial;
    markerMat.map?.dispose();
    markerMat.dispose();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry?.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
  }

  private handleAction(): void {
    if (this.state === 'available') {
      this.state = 'accepted';
      this.onAccept();
    } else if (this.state === 'accepted' && this.progress >= HARVEST_QUEST_TARGET) {
      this.state = 'completed';
      this.markerSprite.visible = false;
      this.close();
      this.onComplete();
    }
    this.refreshDialog();
  }

  private refreshDialog(): void {
    if (this.state === 'available') {
      this.bodyEl.textContent = `Harvest ${HARVEST_QUEST_TARGET} materials with your pickaxe. Reward: ${HARVEST_QUEST_MAT_REWARD} mats + ${HARVEST_QUEST_HP_REWARD} HP.`;
      this.actionBtn.textContent = 'Accept Quest';
      this.actionBtn.disabled = false;
    } else if (this.state === 'accepted') {
      this.bodyEl.textContent = `Progress: ${this.progress} / ${HARVEST_QUEST_TARGET} mats harvested this match.`;
      this.actionBtn.textContent =
        this.progress >= HARVEST_QUEST_TARGET ? 'Claim Reward' : 'Keep harvesting…';
      this.actionBtn.disabled = this.progress < HARVEST_QUEST_TARGET;
    } else {
      this.bodyEl.textContent = 'Quest complete — thanks for the haul!';
      this.actionBtn.textContent = 'Done';
      this.actionBtn.disabled = true;
    }
  }
}
